import { is } from "@babel/types";
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
  Typography,
} from "@mui/material";
import Checkbox from "@mui/material/Checkbox";
import Grid from "@mui/material/Grid";
import { AxiosError } from "axios";
import { enqueueSnackbar } from "notistack";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  useStatAnalyticsPlayersTimingStatistics,
  useStatAnalyticsPlayersTimingStatisticsByDuration,
} from "@/api/hooks/stat-analytics-hooks";
import { filterDataByFilters } from "@/lib/statistics-analytic-utils";
import { DataOutdatedAlert } from "@/module/service-configs-module/statistics-visualization-module/components/data-outdated-alert";
import { SelectFilterMessage } from "@/module/service-configs-module/statistics-visualization-module/components/select-filter-message";
import { Preloader } from "@/module/service-configs-module/statistics-visualization-module/first-svetofor/totals-page";
import MedianTimingsChart from "@/module/service-configs-module/statistics-visualization-module/second-timings/charts/mediant-timings";
import TimingsActionChart from "@/module/service-configs-module/statistics-visualization-module/second-timings/charts/timing-actions-chart";
import {
  MoneyFlowRequestDto,
  PlayerTimingsDurationPointDto,
  PlayerTimingsPeriodDto,
} from "@/shared/api";

export const defaultTimingFilters = {
  actionStages: ["flop", "preflop", "river", "turn"],
  actionTypes: ["bet", "call", "check", "fold", "raise"],
  playerPositions: [0, 1, 2, 3, 4, 5, 6, 7, 8],
};

type TradingPageProps = {
  filters: MoneyFlowRequestDto | null;
  isDataOutdated: boolean;
};

export const TradingPage = ({ filters, isDataOutdated }: TradingPageProps) => {
  const { t } = useTranslation();
  const isInitialized = useRef(false);
  const [timingFilters, setTimingFilters] =
    useState<typeof defaultTimingFilters>(defaultTimingFilters);

  const {
    data: statAnalyticsTimingResponse,
    refetch: refetchTimings,
    isLoading: isLoadingTimings,
    isRefetching: isRefetchingTimings,
    error: timingsError,
    status: timingStatus,
  } = useStatAnalyticsPlayersTimingStatistics({
    domain: filters?.game_criteria.room_id || "",
    moneyFlowRequestDto: filters as MoneyFlowRequestDto,
    queryOptions: {
      enabled: !!filters,
      retry: false,
    },
  });

  useEffect(() => {
    if (timingStatus && timingStatus === "error") {
      const message = (timingsError as unknown as AxiosError<{ message: string }>)?.response
        ?.data.message;
      enqueueSnackbar(`${message?.slice ? message.slice(0, 500) : message}`, {
        variant: "error",
        autoHideDuration: 2000,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timingStatus]);

  useEffect(() => {
    if (statAnalyticsTimingResponse) {
      isInitialized.current = !!statAnalyticsTimingResponse;
    }
  }, [statAnalyticsTimingResponse]);

  const {
    data: statAnalyticsPlayersTimingStatisticsByDurationResponse,
    refetch: refetchTByDuration,
    isLoading: isLoadingTimingsByDuration,
    isRefetching: isRefetchingTimingsByDuration,
    error: timingByDurationError,
    status: timingByDurationStatus,
  } = useStatAnalyticsPlayersTimingStatisticsByDuration({
    domain: filters?.game_criteria.room_id || "",
    moneyFlowRequestDto: filters as MoneyFlowRequestDto,
    queryOptions: {
      enabled: !!filters,
      refetchOnWindowFocus: false,
      retry: false,
    },
  });

  useEffect(() => {
    if (timingByDurationStatus && timingByDurationStatus === "error") {
      const message = (timingByDurationError as unknown as AxiosError<{ message: string }>)
        ?.response?.data.message;
      enqueueSnackbar(`${message?.slice ? message.slice(0, 500) : message}`, {
        variant: "error",
        autoHideDuration: 2000,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timingByDurationStatus]);

  useEffect(() => {
    if (filters) {
      refetchTimings();
      refetchTByDuration();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const [filteredTimings, filteredTimingsByDuration] = useMemo(() => {
    return [
      filterDataByFilters(
        statAnalyticsTimingResponse?.data?.periodStatistics ?? [],
        timingFilters
      ) as PlayerTimingsPeriodDto[],
      filterDataByFilters(
        statAnalyticsPlayersTimingStatisticsByDurationResponse?.data?.periodStatistics ?? [],
        timingFilters
      ) as PlayerTimingsDurationPointDto[],
    ];
  }, [
    statAnalyticsTimingResponse,
    statAnalyticsPlayersTimingStatisticsByDurationResponse,
    timingFilters,
  ]);

  const handleFilterElementChange = (
    event: SelectChangeEvent<string[] | number[]>,
    key: keyof typeof timingFilters
  ) => {
    const {
      target: { value },
    } = event;
    const newValue = typeof value === "string" ? value.split(",") : value;
    setTimingFilters({ ...timingFilters, [key]: newValue });
  };

  return (
    <Grid
      container
      rowSpacing={2}
      columnSpacing={2}
      sx={{
        paddingY: 2,
      }}
    >
      {isLoadingTimings && !isInitialized.current ? (
        <CircularProgress sx={{ margin: "96px auto 32px" }} />
      ) : (
        <>
          {filters ? (
            <>
              <DataOutdatedAlert isDataOutdated={isDataOutdated} />

              <Grid size={{ xs: 12, md: 12 }}>
                <Card elevation={0}>
                  <CardContent sx={{ height: "80%" }}>
                    <Box
                      sx={{
                        display: "flex",
                        gap: 2,
                      }}
                    >
                      <FormControl size="small" sx={{ minWidth: 200 }}>
                        <InputLabel id="action-label">{t("selectAction")}</InputLabel>
                        <Select
                          labelId="action-label"
                          multiple
                          value={timingFilters.actionTypes}
                          onChange={(e) => handleFilterElementChange(e, "actionTypes")}
                          input={<OutlinedInput label={t("selectAction")} />}
                          renderValue={(selected) => selected.join(", ")}
                        >
                          {defaultTimingFilters.actionTypes.map((option) => (
                            <MenuItem key={option} value={option}>
                              <Checkbox
                                checked={timingFilters.actionTypes.indexOf(option) > -1}
                                size="small"
                              />
                              <ListItemText primary={option} />
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>

                      <FormControl size="small" sx={{ minWidth: 200 }}>
                        <InputLabel id="stage-label">{t("selectStage")}</InputLabel>
                        <Select
                          labelId="stage-label"
                          multiple
                          value={timingFilters.actionStages}
                          onChange={(e) => handleFilterElementChange(e, "actionStages")}
                          input={<OutlinedInput label={t("selectStage")} />}
                          renderValue={(selected) => selected.join(", ")}
                        >
                          {defaultTimingFilters.actionStages.map((option) => (
                            <MenuItem key={option} value={option}>
                              <Checkbox
                                checked={timingFilters.actionStages.indexOf(option) > -1}
                                size="small"
                              />
                              <ListItemText primary={option} />
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>

                      <FormControl size="small" sx={{ minWidth: 200 }}>
                        <InputLabel id="position-label">{t("selectPosition")}</InputLabel>
                        <Select
                          labelId="position-label"
                          multiple
                          value={timingFilters.playerPositions}
                          onChange={(e) => handleFilterElementChange(e, "playerPositions")}
                          input={<OutlinedInput label={t("selectPosition")} />}
                          renderValue={(selected) => selected.join(", ")}
                        >
                          {defaultTimingFilters.playerPositions.map((option) => (
                            <MenuItem key={option} value={option}>
                              <Checkbox
                                checked={timingFilters.playerPositions.indexOf(option) > -1}
                                size="small"
                              />
                              <ListItemText primary={option} />
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              <Grid size={{ xs: 12, md: 12 }}>
                <Card sx={{ height: 700 }}>
                  <CardContent
                    sx={{ height: "90%", display: "flex", flexDirection: "column" }}
                  >
                    <Typography
                      variant="h6"
                      gutterBottom
                      sx={{
                        textAlign: "center",
                      }}
                    >
                      {t("medianTimings")}
                    </Typography>
                    {isLoadingTimings || isRefetchingTimings ? (
                      <Preloader />
                    ) : (
                      <MedianTimingsChart data={filteredTimings} />
                    )}
                  </CardContent>
                </Card>
              </Grid>
              <Grid size={{ xs: 12, md: 12 }}>
                <Card sx={{ height: 500 }}>
                  <CardContent
                    sx={{ height: "90%", display: "flex", flexDirection: "column" }}
                  >
                    <Typography
                      variant="h6"
                      gutterBottom
                      sx={{
                        textAlign: "center",
                      }}
                    >
                      {t("medianTimings")}
                    </Typography>
                    {isLoadingTimingsByDuration || isRefetchingTimingsByDuration ? (
                      <Preloader />
                    ) : (
                      <TimingsActionChart data={filteredTimingsByDuration} />
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </>
          ) : (
            <SelectFilterMessage />
          )}
        </>
      )}
    </Grid>
  );
};
